/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2026 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '@/cart/api';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { state } from '@/cart/lib/state';
import type { AvailableFreeGiftRule } from '@/cart/data/models';
import { transformAvailableFreeGifts } from '@/cart/data/transforms';
import {
  GUEST_AVAILABLE_FREE_GIFTS_QUERY,
  CUSTOMER_AVAILABLE_FREE_GIFTS_QUERY,
} from './graphql/GetAvailableFreeGiftsForCartQuery';

/**
 * Lazily fetches the free-gift catalog payload for the current cart.
 * Kept separate from `getCartData`/`CART_FRAGMENT` so the heavy products
 * payload is only requested when `has_available_free_gifts` is true.
 */
export const getAvailableFreeGiftsForCart = async (): Promise<
  AvailableFreeGiftRule[]
> => {
  if (state.authenticated) {
    return fetchGraphQl(CUSTOMER_AVAILABLE_FREE_GIFTS_QUERY, {
      method: 'POST',
    }).then(({ errors, data }) => {
      if (errors) {
        handleFetchError(errors);
        return [];
      }

      return transformAvailableFreeGifts(data?.cart?.available_free_gifts);
    });
  }

  const cartId = state.cartId;
  if (!cartId) throw new Error('No cart ID found');

  return fetchGraphQl(GUEST_AVAILABLE_FREE_GIFTS_QUERY, {
    method: 'POST',
    cache: 'no-cache',
    variables: { cartId },
  }).then(({ errors, data }) => {
    if (errors) {
      handleFetchError(errors);
      return [];
    }

    return transformAvailableFreeGifts(data?.cart?.available_free_gifts);
  });
};
